import { category, isNormal } from './bowel';
import { DAY_MS, MONTH_LONG, daysBetween, longDate, toIso, weekStart } from './dates';
import { bin, f0, f1, GREEN, num, pl } from './format';
import { BRISTOL, BRN, BRN_FG, CATEGORY_CHIP, GRN, GRN_FG, RAMP_FG, RUST, RUST_TEXT } from './palette';
import { FIBRE_ERROR, FIBRE_LOADING } from './fibre';
import { weekName } from './weeks';

// The calendar (Wellness Calendar Simple's `cal`, phase lane off) and the
// day detail panel (Wellness Day Detail's `detail`) for each Daily Log tab,
// ported from the design's cal() and per-tab detail blocks.

const ZERO_CHIP = { bg: '#fff', ring: 'inset 0 0 0 1.5px #CFC4BB' };
const MISS_CHIP = { bg: '#fff', ring: 'inset 0 0 0 1px #E3DCD5', txt: '—' };

const STYLE = {
  zero: ['#fff', '#1f1f1f', 'inset 0 0 0 1.5px #CFC4BB'],
  plain: ['#fff', '#1f1f1f', 'inset 0 0 0 1px #E3DCD5'],
  miss: ['#fff', '#8a8a8a', 'inset 0 0 0 1px #E3DCD5'],
  na: ['#F7F4F1', '#A39A93', 'none'],
  fut: ['#fff', '#C8C8C8', 'inset 0 0 0 1px #F4F0EC'],
};

const pctBins = ['≤20%', '≤40%', '≤60%', '≤80%', '≥81%'];

/**
 * One day's value on a tab's calendar.
 * @returns {{ st: 'val'|'zero'|'plain'|'miss', val?: string, idx?: number, txt: string }}
 */
export function dayValue(ctx, tab, iso) {
  if (tab === 'sym') {
    const d = ctx.sym[iso];
    if (!d) return { st: 'miss', txt: 'No check-in recorded' };
    if (d.free) return { st: 'zero', val: '0', txt: '0/5 — client confirmed no gut symptoms' };
    if (d.avg === 0) return { st: 'zero', val: '0', txt: '0/5 daily average' };
    return { st: 'val', val: f1(d.avg), idx: bin(d.avg, 5), txt: `${f1(d.avg)}/5 daily average` };
  }
  if (tab === 'bowel') {
    const B = ctx.bowel[iso];
    if (!B) return { st: 'miss', txt: 'Bowel movements not recorded' };
    const typed = B.filter((m) => m.ty != null);
    const good = typed.filter((m) => isNormal(m.ty)).length;
    const share = typed.length ? (good / typed.length) * 100 : 0;
    return {
      st: 'val',
      val: String(B.length),
      idx: bin(Math.max(share, 1), 100),
      txt: `${pl(B.length, 'movement')} · ${good} in Bristol 3–4`,
    };
  }
  const status = ctx.fibreStatus(iso);
  if (status === 'loading') return { st: 'miss', val: '…', txt: FIBRE_LOADING };
  if (status === 'error') return { st: 'miss', txt: FIBRE_ERROR };
  const g = ctx.fibre[iso];
  if (g == null) return { st: 'miss', txt: 'Fibre not logged' };
  if (!ctx.fibreTarget) return { st: 'plain', val: f0(g), txt: `${f0(g)} g fibre · no target set` };
  return {
    st: 'val',
    val: f0(g),
    idx: bin(Math.min(g / ctx.fibreTarget, 1) * 100, 100),
    txt: `${f0(g)} g of ${f0(ctx.fibreTarget)} g target${g >= ctx.fibreTarget ? ' · target met' : ''}`,
  };
}

function legendOf(ctx, tab) {
  if (tab === 'sym') {
    return {
      ramp: RUST,
      fg: RAMP_FG,
      rampTitle: 'DAILY AVERAGE SEVERITY · 0–5',
      bins: ['0.1–1', '1.1–2', '2.1–3', '3.1–4', '4.1–5'],
      legend: [{ ...ZERO_CHIP, txt: '0', label: 'Recorded 0 · symptom-free' }, { ...MISS_CHIP, label: 'No check-in' }],
      sub: 'Daily average of the 4 scored symptoms',
    };
  }
  if (tab === 'bowel') {
    return {
      ramp: BRN,
      fg: BRN_FG,
      rampTitle: '% NORMAL STOOLS (TYPE 3–4) · number in day = movements logged',
      bins: ['1–20%', '21–40%', '41–60%', '61–80%', '81–100%'],
      legend: [{ ...MISS_CHIP, label: 'Not recorded' }],
      sub: 'Movements per day, shaded by share of normal stools',
    };
  }
  return {
    ramp: GRN,
    fg: GRN_FG,
    rampTitle: ctx.fibreTarget ? `FIBRE · % OF ${f0(ctx.fibreTarget)} g TARGET · number = grams` : 'FIBRE · number = grams · no target set',
    bins: ctx.fibreTarget ? pctBins : [],
    legend: [{ ...MISS_CHIP, label: 'Not logged' }],
    sub: 'Fibre logged per day (g)',
  };
}

/**
 * Month grid, Monday first, for the month `{ year, month }` (0-based).
 * Days outside the fetched window are greyed and can't be selected.
 * @param {{ canPrev: boolean, canNext: boolean }} nav
 */
export function calendar(ctx, tab, mon, selected, nav) {
  const L = legendOf(ctx, tab);
  const first = Date.UTC(mon.year, mon.month, 1);
  const last = Date.UTC(mon.year, mon.month + 1, 0);
  const offset = (new Date(first).getUTCDay() + 6) % 7;
  const cells = Array.from({ length: offset }, () => ({ blank: true }));
  for (let t = first; t <= last; t += DAY_MS) {
    const iso = toIso(t);
    const fut = iso > ctx.today;
    const pre = iso < ctx.historyStart;
    const v = fut ? { st: 'fut', txt: '' } : pre ? { st: 'na', txt: 'Before the fetched year' } : dayValue(ctx, tab, iso);
    const S = v.st === 'val' ? [L.ramp[v.idx], L.fg[v.idx], 'none'] : STYLE[v.st];
    cells.push({
      blank: false,
      iso,
      day: new Date(t).getUTCDate(),
      val: v.st === 'miss' ? v.val || '—' : v.val || '',
      bg: S[0],
      fg: S[1],
      dop: v.st === 'na' ? '.7' : '1',
      ring: iso === selected ? '0 0 0 2px #fff, 0 0 0 4px #1f1f1f' : S[2],
      clickable: !fut && !pre,
      tip: fut ? '' : `${longDate(iso)} · ${v.txt}`,
      phW: '0',
      phBg: 'transparent',
      phLabel: '',
      phFg: '#555',
    });
  }
  return {
    title: 'Daily calendar',
    sub: `${L.sub} · click a day for details`,
    month: `${MONTH_LONG[mon.month]} ${mon.year}`,
    cells,
    rampTitle: L.rampTitle,
    bins: L.bins.map((label, j) => ({ bg: L.ramp[j], label })),
    legend: L.legend,
    canPrev: nav.canPrev,
    canNext: nav.canNext,
  };
}

const ratingTxt = (v) => (v == null ? 'Not recorded' : `${num(Math.round(v * 10) / 10)}/3`);

/** The Day Detail panel ("DAILY LOG") for one day on a tab. */
export function dayDetail(ctx, tab, iso) {
  const ws = weekStart(iso);
  const s = ctx.sym[iso];
  const B = ctx.bowel[iso];
  const logged = Boolean(s || B);
  const d = {
    date: longDate(iso),
    sub: `${weekName(ctx, ws)} · day ${daysBetween(ws, iso) + 1} of 7`,
    ctx: s ? (s.free ? 'Check-in · symptom-free confirmed' : 'Check-in') : B ? 'Bowel movements only · no symptom check-in' : 'No check-in',
    ctxBg: s ? GREEN : B ? '#A3826C' : 'transparent',
    ctxRing: logged ? 'none' : 'inset 0 0 0 1px #CFC4BB',
    rows: [],
    facts: [],
    items: [],
    hasRows: false,
    hasFacts: false,
    hasItems: false,
    foot: '',
  };

  if (tab === 'sym') {
    Object.assign(d, {
      valueLabel: 'DAILY AVERAGE',
      value: s ? f1(s.avg) : '—',
      unit: s ? '/5' : '',
      valueFg: s && s.avg > 0 ? RUST_TEXT[bin(s.avg, 5)] : '#1f1f1f',
      state: !s ? 'Nothing was logged. Days with no check-in are left out of weekly averages.'
        : s.free ? '0/5 — client confirmed no gut symptoms'
          : 'Scored symptoms not logged count as 0',
      foot: s ? `Average = ${num(s.sum)} ÷ 4 (not logged = 0)` : 'Days with no check-in are never counted as 0',
    });
    const rows = Object.entries(s?.sym || {}).sort((a, b) => b[1] - a[1]);
    if (rows.length) {
      d.hasRows = true;
      d.rowsTitle = `SYMPTOMS · ${rows.length} LOGGED`;
      d.rows = rows.map(([name, v]) => ({
        name,
        txt: num(v),
        w: `${(v / 5) * 100}%`,
        bg: RUST[bin(Math.max(v, 0.1), 5)],
        ring: v === 0 ? 'inset 0 0 0 1px #CFC4BB' : 'none',
      }));
    }
    return d;
  }

  if (tab === 'bowel') {
    const good = (B || []).filter((m) => isNormal(m.ty)).length;
    const inc = (B || []).filter((m) => m.complete === false).length;
    Object.assign(d, {
      valueLabel: 'MOVEMENTS',
      value: B ? String(B.length) : '—',
      unit: '',
      valueFg: '#1f1f1f',
      state: B ? `${good} of ${B.length} in Bristol Type 3–4 · ${inc} incomplete` : 'Bowel movements not recorded.',
      hasItems: Boolean(B),
      itemsTitle: 'BOWEL MOVEMENTS · BRISTOL STOOL SCALE',
      items: (B || []).map((m) => {
        const c = category(m.ty);
        const chip = CATEGORY_CHIP[c] || ['#F1ECE8', '#6b6b6b'];
        return {
          time: m.time || '—',
          title: m.ty ? `Type ${m.ty}` : 'Type not recorded',
          typeBg: m.ty ? BRISTOL[m.ty][0] : '#F1ECE8',
          chip: c || '',
          chipBg: chip[0],
          chipFg: chip[1],
          meta: [
            { k: 'Urgency', v: ratingTxt(m.u) },
            { k: 'Straining', v: ratingTxt(m.s) },
          ],
          status: m.complete == null ? 'Evacuation not recorded' : m.complete ? 'Complete' : 'Incomplete',
          statusBg: m.complete == null ? '#F4F0EC' : m.complete ? '#E3F0E7' : '#FBE3DF',
          statusFg: m.complete == null ? '#6b6b6b' : m.complete ? GREEN : '#9E2F24',
        };
      }),
      foot: 'Bristol Stool Scale 1–7 · 3–4 = normal',
    });
    return d;
  }

  const fStatus = ctx.fibreStatus(iso);
  const g = fStatus === 'ready' ? ctx.fibre[iso] ?? null : null;
  const T = ctx.fibreTarget;
  const fibreTxt = fStatus === 'error' ? 'couldn’t be loaded'
    : fStatus === 'loading' || (g != null && T === undefined) ? 'loading…'
      : g == null ? 'not logged'
        : !T ? `${f0(g)} g · no target set`
          : g >= T ? 'target met'
            : `${f0(T - g)} g under target`;
  Object.assign(d, {
    valueLabel: 'FIBRE',
    value: fStatus === 'loading' ? '…' : g == null ? '—' : f0(g),
    unit: g == null ? '' : T ? `/${f0(T)} g` : 'g',
    valueFg: '#1f1f1f',
    state: `Fibre ${fibreTxt}`,
    foot: T ? `Fibre target ${f0(T)} g (current)` : T === null ? 'No fibre target set' : '',
  });
  const rows = [];
  if (g != null && T) rows.push({ name: 'Fibre (g)', txt: f0(g), w: `${Math.min(100, (g / T) * 100)}%`, bg: '#5E8C5A', ring: 'none' });
  if (rows.length) {
    d.hasRows = true;
    d.rowsTitle = 'INTAKE VS TARGET';
    d.rows = rows;
  }
  return d;
}
